import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { css } from "styled-system/css";
import { ed } from "./editorial/tokens";

interface Node {
  id: string;
  name: string;
  avatarUrl: string | null;
  reviewsGiven: number;
  reviewsReceived: number;
  teamId: string | null;
  teamName: string | null;
  // D3 simulation adds these
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface Link {
  source: string | Node;
  target: string | Node;
  value: number;
}

interface ReviewGraphProps {
  nodes: Node[];
  links: Link[];
  height?: number;
}


export function ReviewGraph({ nodes, links, height = 500 }: ReviewGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    content: string;
  } | null>(null);
  const [width, setWidth] = useState(800);

  // Measure container width on mount and resize
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setWidth(entry.contentRect.width);
      }
    });

    observer.observe(container);
    // Set initial width
    setWidth(container.clientWidth);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!svgRef.current || nodes.length === 0 || width === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Editorial palette: every node gray, the busiest reviewer in the
    // emphasis color, since that's who the graph is usually asked about.
    const topReviewerId = nodes.reduce<Node | null>(
      (top, n) => (!top || n.reviewsGiven > top.reviewsGiven ? n : top),
      null,
    )?.id;
    const pad = 24;

    // Create the simulation
    const simulation = d3
      .forceSimulation(nodes as d3.SimulationNodeDatum[])
      .force(
        "link",
        d3
          .forceLink(links)
          .id((d: any) => d.id)
          .distance(100)
          .strength((d: any) => Math.min(d.value / 10, 1)),
      )
      .force("charge", d3.forceManyBody().strength(-200))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius(30));

    // Create arrow markers for directed edges
    svg
      .append("defs")
      .selectAll("marker")
      .data(["arrow"])
      .enter()
      .append("marker")
      .attr("id", "arrow")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 25)
      .attr("refY", 0)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr("fill", ed.markStrong)
      .attr("d", "M0,-5L10,0L0,5");

    // Create container groups
    const g = svg.append("g");

    // Create links (edges)
    const link = g
      .append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(links)
      .enter()
      .append("line")
      .attr("stroke", ed.rule)
      .attr("stroke-opacity", 1)
      .attr("stroke-width", (d: any) => Math.sqrt(d.value))
      .attr("marker-end", "url(#arrow)");

    // Create node groups
    const node = g
      .append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(nodes)
      .enter()
      .append("g")
      .call(
        d3
          .drag<SVGGElement, Node>()
          .on("start", (event, d) => {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on("drag", (event, d) => {
            d.fx = event.x;
            d.fy = event.y;
          })
          .on("end", (event, d) => {
            if (!event.active) simulation.alphaTarget(0);
            d.fx = null;
            d.fy = null;
          }),
      );

    // Add circles for nodes
    node
      .append("circle")
      .attr("r", (d: Node) => 8 + Math.sqrt(d.reviewsGiven + d.reviewsReceived))
      .attr("fill", (d: Node) => (d.id === topReviewerId ? ed.emph : ed.mark))
      .attr("stroke", ed.paper)
      .attr("stroke-width", 2)
      .style("cursor", "pointer");

    // Add labels
    node
      .append("text")
      .text((d: Node) => d.name.split(" ")[0] || d.name) // First name only
      .attr("x", 0)
      .attr(
        "y",
        (d: Node) => 16 + Math.sqrt(d.reviewsGiven + d.reviewsReceived),
      )
      .attr("text-anchor", "middle")
      .attr("font-size", "11px")
      .attr("font-family", ed.sans)
      .attr("fill", ed.ink2)
      .attr("pointer-events", "none");

    // Tooltip interactions
    node
      .on("mouseover", (event, d: Node) => {
        const rect = svgRef.current?.getBoundingClientRect();
        if (rect) {
          setTooltip({
            x: event.clientX - rect.left,
            y: event.clientY - rect.top - 10,
            content: `${d.name}\nReviews given: ${d.reviewsGiven}\nReviews received: ${d.reviewsReceived}${d.teamName ? `\nTeam: ${d.teamName}` : ""}`,
          });
        }
      })
      .on("mouseout", () => {
        setTooltip(null);
      });

    // Update positions on simulation tick
    simulation.on("tick", () => {
      // Keep nodes inside the frame; sparse graphs otherwise drift off the top
      node.attr("transform", (d: any) => {
        d.x = Math.max(pad, Math.min(width - pad, d.x));
        d.y = Math.max(pad, Math.min(height - pad, d.y));
        return `translate(${d.x},${d.y})`;
      });

      link
        .attr("x1", (d: any) => d.source.x)
        .attr("y1", (d: any) => d.source.y)
        .attr("x2", (d: any) => d.target.x)
        .attr("y2", (d: any) => d.target.y);
    });

    // Zoom behavior
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 3])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });

    svg.call(zoom);

    return () => {
      simulation.stop();
    };
  }, [nodes, links, width, height]);

  if (nodes.length === 0) {
    return (
      <div className={styles.empty}>
        No review data available for this time period.
      </div>
    );
  }

  return (
    <div ref={containerRef} className={styles.container}>
      <svg ref={svgRef} width={width} height={height} className={styles.svg} />
      {tooltip && (
        <div
          className={styles.tooltip}
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {tooltip.content.split("\n").map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}
      <p className={styles.legend}>
        Each circle is a person, sized by reviews given plus received. Arrows
        run from reviewer to author; thicker lines mean more reviews. The
        busiest reviewer is in color. Drag to rearrange, scroll to zoom.
      </p>
    </div>
  );
}

const styles = {
  container: css({
    position: "relative",
    width: "100%",
    overflow: "hidden",
  }),
  svg: css({
    display: "block",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "border.default",
  }),
  empty: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "300px",
    color: "fg.muted",
    fontSize: "sm",
  }),
  tooltip: css({
    position: "absolute",
    background: "bg.default",
    border: "1px solid",
    borderColor: "fg.default",
    padding: "6px 10px",
    fontFamily: "mono",
    fontSize: "xs",
    color: "fg.default",
    pointerEvents: "none",
    transform: "translate(-50%, -100%)",
    zIndex: 10,
    whiteSpace: "nowrap",
  }),
  legend: css({
    mt: 2,
    fontSize: "xs",
    color: "fg.muted",
    maxWidth: "720px",
  }),
};
